import Switch from '@mui/material/Switch'
import { SyntheticEvent, useEffect, useState } from 'react'
import { useMatch, useParams, useResolvedPath } from 'react-router'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import FormHelperText from '@mui/material/FormHelperText'
import Paper from '@mui/material/Paper'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Wrapper from '../common/Wrapper'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

import { useBillingApi } from '../../api/billing/billing-context'
import { ValidatedInput } from '../common/InputWithValidation'
import { AiCustomerStatus, CustomerBillingType, CustomerUpdate, NewCustomer, Role } from 'common-billing-server/types'
import { isRoleAuthorized } from 'common-billing-server/role'
import { Path } from '../../routes/path'
import { notEmpty } from '../../validators'
import { commonTextFieldProps, minHeight } from '../../theme/common-styles'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import AddIcon from '@mui/icons-material/Add'
import ArchiveIcon from '@mui/icons-material/Archive'
import UnarchiveIcon from '@mui/icons-material/Unarchive'
import SaveIcon from '@mui/icons-material/Save'
import Alert from '@mui/material/Alert'
import { useStatefulNavigate } from '../common/hooks/hook-stateful-navigate'
import { ONE_MINUTE_IN_MS } from '../../format-date'
import { CustomerRtuManagement } from './CustomerRtuManagement'
import { CustomerApplianceLicenseManagement } from './CustomerApplianceLicenseManagement'
import { useAuth } from '../login/Auth'
import { useConfirmationDialog } from '../common/ConfirmationDialog'
import { CustomerLinks } from './CustomerLinks'
import { ImportNetSuiteCustomer } from './ImportNetSuiteCustomer'

const usdFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
})
const formatMicroUsd = (microUsd: number) => usdFormatter.format(microUsd / 1_000_000)

export const CreateEditCustomerPage = () => {
    const billingApi = useBillingApi()
    const { user } = useAuth()
    const showConfirmation = useConfirmationDialog()
    const { navigateBack } = useStatefulNavigate()
    const urlParams = useParams()
    const customerId: string | undefined = urlParams.customerId
    const resolvedPath = useResolvedPath(Path.editCustomer)
    const isEditingCustomer = !!useMatch({ path: resolvedPath.pathname, end: true }) && !!customerId
    const canImport = !!user && isRoleAuthorized({ role: user.role, minimumAllowedRole: Role.admin })
    const [creationSource, setCreationSource] = useState<'manual' | 'netsuite'>('netsuite')
    const showSourceTabs = !isEditingCustomer && canImport

    const [name, setName] = useState('')
    const [customerNumber, setCustomerNumber] = useState('')
    const [deleted, setDeleted] = useState(false)
    const [managedHostingEnabled, setManagedHostingEnabled] = useState(false)
    const [comment, setComment] = useState('')
    const [billingType, setBillingType] = useState(CustomerBillingType.usageBased)
    const [billingStartsAt, setBillingStartsAt] = useState<Date | null>(new Date())
    // New agreements default to the leg-weighted model from day one — the start of the current
    // UTC month, so the customer's first billing period (which starts at the month boundary) is
    // covered. Editing starts from the customer's recorded value.
    const [legWeightedRtusStartAt, setLegWeightedRtusStartAt] = useState<Date | null>(() => {
        if (isEditingCustomer) return null
        const now = new Date()
        return new Date(now.getUTCFullYear(), now.getUTCMonth(), 1)
    })
    const [fetchedLegWeightedRtusStartAt, setFetchedLegWeightedRtusStartAt] = useState<Date | null>(null)
    const [aiStatus, setAiStatus] = useState(AiCustomerStatus.trial)
    const [loadedAiStatus, setLoadedAiStatus] = useState(AiCustomerStatus.trial)
    const [aiSpend, setAiSpend] = useState<{ lifetime: number; period: number }>()
    const [trialCreditMicroUsd, setTrialCreditMicroUsd] = useState<number>()

    const [isLoading, setIsLoading] = useState(false)
    const [open, setOpen] = useState(false)
    const [legWeightedPickerOpen, setLegWeightedPickerOpen] = useState(false)
    const [hasSubmittedForm, setHasSubmittedForm] = useState(false)
    const [validationErrors, setValidationErrors] = useState({
        name: undefined as string | undefined,
        comment: undefined as string | undefined,
        customerNumber: undefined as string | undefined,
    })
    const updateValidationError = (key: keyof typeof validationErrors, value: string | undefined) => {
        setValidationErrors((prevState) => ({ ...prevState, [key]: value }))
    }
    const formContainsInvalidInput = Object.values(validationErrors).some((error) => error)
    const isSuper = !!user && isRoleAuthorized({ role: user.role, minimumAllowedRole: Role.super })

    const { addFailedOperation, removeFailedOperation, errorBanner } = useFailedOperationBanner()

    useEffect(() => {
        const operationId = 'fetch-customer'

        async function fetchCustomer(id: string) {
            removeFailedOperation(operationId)
            setIsLoading(true)
            try {
                const response = await billingApi.listCustomers({ filter: { customerId: id } })
                const customer = response.items[0]
                if (!customer) {
                    throw new Error(`No customer found with id '${id}'`)
                }
                const {
                    name,
                    customerNumber,
                    comment,
                    billingType,
                    billingStartsAt,
                    legWeightedRtusStartAt,
                    aiStatus,
                } = customer
                setName(name)
                setDeleted(customer.deleted)
                setManagedHostingEnabled(customer.managedHostingEnabled === true)
                setCustomerNumber(customerNumber)
                setComment(comment)
                setBillingType(billingType)
                setAiStatus(aiStatus)
                setLoadedAiStatus(aiStatus)
                setAiSpend({ lifetime: customer.aiLifetimeSpendMicroUsd, period: customer.aiPeriodSpendMicroUsd })
                // The trial allowance is global, and turns the spend figure into something actionable.
                try {
                    const settings = await billingApi.getGlobalsettings()
                    setTrialCreditMicroUsd(settings.ai?.trialCreditMicroUsd)
                } catch {
                    setTrialCreditMicroUsd(undefined)
                }

                // 'billingStartsAt' is in UTC time.
                // The date picker shows time in the local timezone, but we pretend that it is showing UTC time,
                // hence we need to modify the time so that it shows the UTC-time.
                billingStartsAt.setTime(
                    billingStartsAt.getTime() + billingStartsAt.getTimezoneOffset() * ONE_MINUTE_IN_MS
                )
                setBillingStartsAt(billingStartsAt)

                if (legWeightedRtusStartAt) {
                    legWeightedRtusStartAt.setTime(
                        legWeightedRtusStartAt.getTime() + legWeightedRtusStartAt.getTimezoneOffset() * ONE_MINUTE_IN_MS
                    )
                    setLegWeightedRtusStartAt(legWeightedRtusStartAt)
                    setFetchedLegWeightedRtusStartAt(new Date(legWeightedRtusStartAt.getTime()))
                } else {
                    setLegWeightedRtusStartAt(null)
                }
            } catch (error) {
                addFailedOperation({
                    id: operationId,
                    message: 'Failed fetching customer',
                    error,
                })
            }
            setIsLoading(false)
        }

        if (isEditingCustomer) {
            void fetchCustomer(customerId)
        }
    }, [customerId, isEditingCustomer])

    const submitCustomer = async () => {
        const operationId = 'update-user'
        removeFailedOperation(operationId)
        setHasSubmittedForm(true)
        if (formContainsInvalidInput) {
            return
        }
        // A half-typed picker value is an Invalid Date, which would serialize as null and
        // silently clear (or omit) a cut-over.
        if (legWeightedRtusStartAt !== null && isNaN(legWeightedRtusStartAt.getTime())) {
            addFailedOperation({ id: operationId, message: 'The leg-weighted RTU cut-over date is not valid' })
            return
        }
        try {
            setIsLoading(true)

            // Since we interpret the local time displayed by the date picker as UTC we need to convert the selected date to UTC.
            // E.g. if the local timezone is "GMT+2" then selecting "2022-07-01 01:55" in the date picker will correspond to "2022-06-30 23:55" UTC time.
            // So we modify the selected date by removing the timezone offset, thus generating the correct UTC date for the backend
            const toUtcMidnight = (value: Date) => {
                const adjusted = new Date(value)
                adjusted.setHours(0, 0, 0, 0)
                adjusted.setTime(adjusted.getTime() - adjusted.getTimezoneOffset() * ONE_MINUTE_IN_MS)
                return adjusted
            }
            const customer: NewCustomer = {
                name,
                customerNumber,
                comment,
                billingType,
                billingStartsAt: toUtcMidnight(billingStartsAt!),
                aiStatus,
            }
            if (isEditingCustomer) {
                const update: CustomerUpdate = { ...customer, managedHostingEnabled }
                // The cut-over rides along only when the admin actually changed it: a rendered
                // one is immutable server-side, and re-sending an untouched one would truncate a
                // non-midnight instant set through the API.
                const cutoverChanged =
                    (legWeightedRtusStartAt?.getTime() ?? null) !== (fetchedLegWeightedRtusStartAt?.getTime() ?? null)
                if (cutoverChanged) {
                    update.legWeightedRtusStartAt = legWeightedRtusStartAt
                        ? toUtcMidnight(legWeightedRtusStartAt)
                        : null
                }
                await billingApi.updateCustomer(customerId!, update)
            } else {
                await billingApi.createCustomer({
                    ...customer,
                    legWeightedRtusStartAt: legWeightedRtusStartAt ? toUtcMidnight(legWeightedRtusStartAt) : null,
                })
            }
            setIsLoading(false)
            navigateBack({ replace: true })
        } catch (error) {
            setIsLoading(false)
            addFailedOperation({
                id: operationId,
                message: `Failed ${isEditingCustomer ? 'updating' : 'creating'} customer`,
                error,
            })
        }
    }

    const createOrUpdateCustomer = (event: SyntheticEvent) => {
        event.preventDefault()
        setHasSubmittedForm(true)
        if (formContainsInvalidInput) return
        if (aiStatus === AiCustomerStatus.active && loadedAiStatus !== AiCustomerStatus.active) {
            showConfirmation(
                () => void submitCustomer(),
                `Activate AI assistant access for ${name}? Active removes the trial-credit limit; configured AI budgets still apply.`,
                { ok: { text: 'Activate' } }
            )
            return
        }
        void submitCustomer()
    }

    const changeArchiveState = () => {
        if (!customerId) return
        showConfirmation(
            () => {
                void (async () => {
                    setIsLoading(true)
                    removeFailedOperation('archive-customer')
                    try {
                        await billingApi.updateCustomer(customerId, { deleted: !deleted })
                        setDeleted(!deleted)
                    } catch (error) {
                        addFailedOperation({
                            id: 'archive-customer',
                            message: 'Failed changing customer archive state',
                            error,
                        })
                    } finally {
                        setIsLoading(false)
                    }
                })()
            },
            deleted
                ? `Restore ${name}?`
                : `Archive ${name}? Existing licenses, installations, user access, and billing remain active.`,
            { ok: { text: deleted ? 'Restore' : 'Archive', startIcon: deleted ? <UnarchiveIcon /> : <ArchiveIcon /> } }
        )
    }

    const sourceTabs = showSourceTabs ? (
        <Tabs
            value={creationSource}
            onChange={(_, value: 'manual' | 'netsuite') => setCreationSource(value)}
            aria-label="Customer source"
            sx={{ mb: 2 }}
        >
            <Tab
                value="netsuite"
                label="Import from NetSuite"
                id="customer-source-netsuite"
                aria-controls="customer-netsuite-panel"
                disabled={isLoading}
            />
            <Tab
                value="manual"
                label="Manual"
                id="customer-source-manual"
                aria-controls="customer-manual-panel"
                disabled={isLoading}
            />
        </Tabs>
    ) : null

    if (showSourceTabs && creationSource === 'netsuite') {
        return (
            <Wrapper>
                {sourceTabs}
                <Paper
                    role="tabpanel"
                    id="customer-netsuite-panel"
                    aria-labelledby="customer-source-netsuite"
                    sx={{ p: 2.5 }}
                >
                    <ImportNetSuiteCustomer />
                </Paper>
            </Wrapper>
        )
    }

    return (
        <Wrapper
            title={isEditingCustomer ? name : undefined}
            actions={
                isEditingCustomer && isSuper ? (
                    <Button
                        variant="outlined"
                        disabled={isLoading}
                        startIcon={deleted ? <UnarchiveIcon /> : <ArchiveIcon />}
                        onClick={changeArchiveState}
                    >
                        {deleted ? 'Restore customer' : 'Archive customer'}
                    </Button>
                ) : undefined
            }
        >
            {sourceTabs}
            {deleted && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    This customer is archived. Existing licenses, installations, user access, and billing are unchanged.
                </Alert>
            )}
            <Paper
                role={showSourceTabs ? 'tabpanel' : undefined}
                id={showSourceTabs ? 'customer-manual-panel' : undefined}
                aria-labelledby={showSourceTabs ? 'customer-source-manual' : undefined}
            >
                <form
                    onSubmit={(e) => void createOrUpdateCustomer(e)}
                    style={{
                        padding: '20px',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <ValidatedInput
                        {...commonTextFieldProps()}
                        type="text"
                        label={'Name'}
                        autoFocus={true}
                        disabled={isLoading}
                        validators={[notEmpty]}
                        value={name}
                        onChange={setName}
                        onValidationError={(error) => updateValidationError('name', error)}
                        showErrorDespiteUntouched={hasSubmittedForm}
                    />

                    <ValidatedInput
                        {...commonTextFieldProps()}
                        type="text"
                        label={'Customer number'}
                        disabled={isLoading}
                        validators={[]}
                        helperText="Optional for internal or development customers."
                        value={customerNumber}
                        onChange={setCustomerNumber}
                        onValidationError={(error) => updateValidationError('customerNumber', error)}
                        showErrorDespiteUntouched={hasSubmittedForm}
                    />

                    <ValidatedInput
                        {...commonTextFieldProps()}
                        type="text"
                        label={'Comment'}
                        disabled={isLoading}
                        validators={[]}
                        value={comment}
                        onChange={setComment}
                        onValidationError={(error) => updateValidationError('comment', error)}
                        showErrorDespiteUntouched={hasSubmittedForm}
                    />

                    <div style={{ width: '100%', minHeight: minHeight('normal') }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                open={open}
                                onOpen={() => setOpen(true)}
                                onClose={() => setOpen(false)}
                                label="Billing start date (UTC)"
                                value={billingStartsAt}
                                onChange={(value) => setBillingStartsAt(value)}
                                format="yyyy-MM-dd"
                                slotProps={{
                                    textField: {
                                        variant: 'standard',
                                        onClick: () => setOpen(true),
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </div>

                    <div style={{ width: '100%', minHeight: minHeight('normal') }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                open={legWeightedPickerOpen}
                                onOpen={() => setLegWeightedPickerOpen(true)}
                                onClose={() => setLegWeightedPickerOpen(false)}
                                label="Leg-weighted RTUs from (UTC)"
                                value={legWeightedRtusStartAt}
                                onChange={(value) => setLegWeightedRtusStartAt(value)}
                                format="yyyy-MM-dd"
                                disabled={isLoading}
                                slotProps={{
                                    textField: {
                                        variant: 'standard',
                                        onClick: () => setLegWeightedPickerOpen(true),
                                        helperText: isEditingCustomer
                                            ? 'Set once the terms change is agreed: billing periods from this date consume one Output RTU per leg (a redundant output counts 2). Empty keeps the flat rule. Locked once an invoice has rendered leg-weighted.'
                                            : 'New agreements start on the leg-weighted model (one Output RTU per leg; a redundant output counts 2). Clear to keep the flat rule.',
                                    },
                                    field: { clearable: true },
                                }}
                            />
                        </LocalizationProvider>
                    </div>

                    <FormControl disabled={true} fullWidth style={{ margin: '8px 0px 0px 0px' }}>
                        <FormLabel id="role-radio-button-group-label">Billing type</FormLabel>
                        <RadioGroup row aria-labelledby="row-radio-button-group-label" name="row-radio-button-group">
                            {Object.values(CustomerBillingType).map((type) => (
                                <FormControlLabel
                                    key={type}
                                    value={type}
                                    checked={type === billingType}
                                    onChange={(_, checked) => {
                                        if (checked) setBillingType(type)
                                    }}
                                    control={<Radio />}
                                    label={type}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>

                    {isEditingCustomer && isSuper && (
                        <FormControl fullWidth>
                            <FormControlLabel
                                label="Enable managed hosting"
                                control={
                                    <Switch
                                        checked={managedHostingEnabled}
                                        disabled={isLoading}
                                        onChange={(_, checked) => setManagedHostingEnabled(checked)}
                                    />
                                }
                            />
                        </FormControl>
                    )}
                    <FormControl fullWidth disabled={!isSuper || isLoading} style={{ margin: '8px 0px 0px 0px' }}>
                        <FormLabel>AI assistant access</FormLabel>
                        <RadioGroup
                            row
                            value={aiStatus}
                            onChange={(event) => setAiStatus(event.target.value as AiCustomerStatus)}
                        >
                            <FormControlLabel value={AiCustomerStatus.trial} control={<Radio />} label="Trial" />
                            <FormControlLabel value={AiCustomerStatus.active} control={<Radio />} label="Active" />
                            <FormControlLabel value={AiCustomerStatus.off} control={<Radio />} label="Off" />
                        </RadioGroup>
                        <FormHelperText>
                            {isSuper
                                ? 'Trial stops the assistant once the global trial credit is spent. Active removes the trial-credit limit; configured customer and global AI budgets still apply. Off disables the assistant for every installation of this customer.'
                                : 'Only a super administrator can change AI assistant access.'}
                        </FormHelperText>
                    </FormControl>

                    {aiSpend ? (
                        <FormControl fullWidth style={{ margin: '8px 0px 0px 0px' }} data-testid="customer-ai-spend">
                            <FormLabel>AI assistant spend</FormLabel>
                            <FormHelperText>
                                {formatMicroUsd(aiSpend.period)} this billing period ·{' '}
                                {formatMicroUsd(aiSpend.lifetime)} in total
                                {aiStatus === AiCustomerStatus.trial && trialCreditMicroUsd !== undefined
                                    ? ` · ${formatMicroUsd(Math.max(trialCreditMicroUsd - aiSpend.lifetime, 0))} of ${formatMicroUsd(trialCreditMicroUsd)} trial credit left`
                                    : ''}
                            </FormHelperText>
                        </FormControl>
                    ) : null}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mt: 1 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={formContainsInvalidInput || isLoading}
                            startIcon={
                                isLoading ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : isEditingCustomer ? (
                                    <SaveIcon />
                                ) : (
                                    <AddIcon />
                                )
                            }
                        >
                            {isEditingCustomer ? 'Update' : 'Create Customer'}
                        </Button>
                    </Box>
                    {errorBanner}
                </form>
            </Paper>

            {isEditingCustomer && (
                <Box sx={{ mt: 2 }}>
                    <CustomerLinks customerId={customerId} />
                </Box>
            )}
            {isEditingCustomer && <CustomerRtuManagement customerId={customerId} customerName={name} />}
            {isEditingCustomer && <CustomerApplianceLicenseManagement customerId={customerId} customerName={name} />}
        </Wrapper>
    )
}
